import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
  Table,
} from "@4uhub/lib4uhub";

import MainApiService from "../../../../services/mainApi.service";
import { IHealthProgramsContentRules } from "../../../../models/health-program-content-rule";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

import SubjectIcon from "@mui/icons-material/Subject";
import { useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";

const HEALTH_PROGRAMS_CONTENT_RULES_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/HealthProgramContentRule/";
const healthProgramsContentRulesService =
  new MainApiService<IHealthProgramsContentRules>(
    HEALTH_PROGRAMS_CONTENT_RULES_ROUTE
  );

const translationPath = "page.4uHcp.hcp.healthProgramsContentRules.";

const HealthProgramsContentRulesPage = () => {
  const navigate = useNavigate();

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [enableFields, setEnableFields] = useState<boolean>(true);
  const [selectedFilter, setSelectedFilter] = useState<ISelectType | null>(
    null
  );
  const filterOptions: ISelectType[] = useMemo(
    () => [
      {
        id: "initialDate",
        name: t(translationPath + "initial_date") || "Initial Date",
        value: "initialDate",
      },
      {
        id: "endDate",
        name: t(translationPath + "end_date") || "End Date",
        value: "endDate",
      },
    ],
    []
  );

  const columns: GridColDef<IHealthProgramsContentRules>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "healthProgram",
      headerName: t(translationPath + "health_program") || "Health Program",
      renderCell: (params) => (params.value ? params.value.name : "-"),
    },
    {
      field: "initialDate",
      headerName: t(translationPath + "initial_date") || "Initial Date",
      maxWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return params.value
          ? format(date, "P", {
              locale: language === "pt-BR" ? ptBR : enUS,
            })
          : "-";
      },
    },
    {
      field: "endDate",
      headerName: t(translationPath + "end_date") || "End Date",
      maxWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return params.value
          ? format(date, "P", {
              locale: language === "pt-BR" ? ptBR : enUS,
            })
          : "-";
      },
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (
    params: GridRowParams<IHealthProgramsContentRules>
  ) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const redirectToContentsPage = (id: string) => {
    navigate(`${id}/contents`);
  };

  const props = useMemo(() => {
    return {
      StartDate: startDate ? startDate : undefined,
      EndDate: endDate ? endDate : undefined,
      TypeFilter:
        selectedFilter && selectedFilter.id == "initialDate"
          ? "start"
          : selectedFilter && selectedFilter.id == "endDate"
          ? "end"
          : undefined,
    };
  }, [startDate, endDate, selectedFilter]);

  return (
    <Table<IHealthProgramsContentRules>
      showDefaultMacroFilters={false}
      service={healthProgramsContentRulesService}
      columns={columns}
      title={t(translationPath + "health_programs_content_rules")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      serviceProps={props}
      macroFilters={() => {
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: "0 0 200px" }}>
              <DateRangeSelect
                setEndDate={setEndDate}
                setStartDate={setStartDate}
                setEnableFields={setEnableFields}
                showFilter={true}
                filterOptions={filterOptions}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
              />
            </Box>

            <Box sx={{ flex: "0 0 170px" }}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "initial_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            <Box sx={{ flex: "0 0 170px" }}>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
          </Stack>
        );
      }}
      rowActions={[
        {
          text: t(translationPath + "contents") || "Contents",
          action: (params) => redirectToContentsPage(params.row.id),
          icon: <SubjectIcon />,
        },
      ]}
      searchInputPlaceHolder={
        t(translationPath + "search") ||
        "Search for a health program content rule"
      }
      addButtonLabel={
        t(translationPath + "add_health_program_content_rule") ||
        "Add Health Program Content Rule"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default HealthProgramsContentRulesPage;
